import {
  useActions,
  useLocations,
  useUpdateLocation,
  useDeleteLocation,
  useResources,
  useUpdateResource,
  useDeleteResource,
  useQueues,
  useUpdateQueue,
  useDeleteQueue,
  useSelectionLists,
  useDeleteSelectionList,
  useUpdateAction,
  useSelectedLocation,
  useDeleteAction,
} from '../../../hooks';
import { BASE_CONFIG, CARD_CONFIG } from './config';

import { SetupAccordion } from '../../molecules';
import {
  LocationDrawer,
  ResourceDrawer,
  QueueDrawer,
  SelectionListDrawer,
  ActionDrawer,
} from '../../molecules/Drawers';
import { useAppContext } from '../../../providers';

import * as classes from './SetupContent.module.css';

/**
 * filter for selection lists that filters on descrption or the description of a selection list value
 * @param  {Local.SetupCardData}  data - array objects to search through
 * @param {string} searchValue
 */
const selectionListFilterFn = (data, searchValue) => {
  const q = searchValue.toLowerCase();
  return (
    data?.filter((item) => {
      if (item?.description.toLowerCase().includes(q)) return true;
      if (item?.values && item?.values.some(({ description }) => description.toLowerCase().includes(q))) return true;
      return false;
    }) || []
  );
};

/**
 * SetupContent organism
 */
export const SetupContent = () => {
  const { ACTIONS, LOCATIONS, RESOURCES, QUEUES, SELECTION_LISTS } = BASE_CONFIG;
  const { selectedPlant } = useAppContext();
  const { selectedLocation } = useSelectedLocation();

  // Actions
  const { data: actions, isLoading: isLoadingActions } = useActions();
  const { mutate: updateAction } = useUpdateAction();
  const { mutate: deleteAction } = useDeleteAction();

  const actionsConfig = {
    ...ACTIONS,
    Drawer: ActionDrawer,
    /** @type {Local.SetupCardsProps['config']} */
    cardConfig: {
      ...CARD_CONFIG(ACTIONS.id),
      childSetups: 'actionQueue',
      manageChildren: false,
      /**
       * Handles deleting an action
       * @param {string} id - The ID of the action to delete
       */
      onDelete: (id) => deleteAction(id),
      /**
       * Handler to update the status of an action
       * @param {Local.SetupCardRecord} action
       */
      onStatus: (action) =>
        updateAction({
          id: action.id,
          input: {
            active: !action.active,
            queueId: action.queue?.id ?? action.queueId ?? '',
            message: action.message ?? '',
            description: action.description,
            locationId: selectedLocation,
            sequence: Number(action.sequence ?? 0),
            showComment: action.showComment ?? false,
            showQuantity: action.showQuantity ?? false,
            showSelectionList: action.showSelectionList ?? false,
            showDynamicInput: action.showDynamicInput ?? false,
            isDefault: action.isDefault ?? false,
            actionPrompt: action.actionPrompt ?? undefined,
          },
        }),
      /** onCopy handler */
      canCopy: true, // Enable copy functionality for actions
    },
  };

  // Locations
  const { data: locations, isLoading: isLoadingLocations } = useLocations(false);
  const { mutate: updateLocation } = useUpdateLocation();
  const { mutate: deleteLocation } = useDeleteLocation();

  const locationsConfig = {
    ...LOCATIONS,
    Drawer: LocationDrawer,
    /** @type {Local.SetupCardsProps['config']} */
    cardConfig: {
      ...CARD_CONFIG(LOCATIONS.id),
      /**
       * Handler to delete a location
       * @param {string} id
       */
      onDelete: (id) => deleteLocation(id),
      /**
       * Handler to update the status of a location
       * @param {Local.SetupCardRecord} location
       */
      onStatus: (location) => updateLocation({ id: location.id, input: { active: !location.active } }),
      onCopy: undefined,
    },
  };

  // Resources
  const { data: resources, isLoading: isLoadingResources } = useResources(true);
  const { mutate: updateResource } = useUpdateResource();
  const { mutate: deleteResource } = useDeleteResource();

  const resourcesConfig = {
    ...RESOURCES,
    Drawer: ResourceDrawer,
    /** @type {Local.SetupCardsProps['config']} */
    cardConfig: {
      ...CARD_CONFIG(RESOURCES.id),
      childSetups: 'queue',
      manageChildren: true,
      /**
       * Handler to delete a resource
       * @param {string} id
       */
      onDelete: (id) => deleteResource(id),
      /**
       * Handler to update the status of a resource
       * @param {Local.SetupCardRecord} resource
       */
      onStatus: (resource) => updateResource({ id: resource.id, input: { active: !resource.active } }),
      onCopy: undefined,
    },
  };

  // Queues
  const { data: queues, isLoading: isLoadingQueues } = useQueues(selectedPlant, true);
  const { mutate: updateQueue } = useUpdateQueue();
  const { mutate: deleteQueue } = useDeleteQueue();

  const queuesConfig = {
    ...QUEUES,
    Drawer: QueueDrawer,
    /** @type {Local.SetupCardsProps['config']} */
    cardConfig: {
      ...CARD_CONFIG(QUEUES.id),
      childSetups: 'resource',
      manageChildren: true,
      /**
       * Handler to delete a queue
       * @param {string} id
       */
      onDelete: (id) => deleteQueue(id),
      /**
       * Handler to update the status of a queue
       * @param {Local.SetupCardRecord} queue
       */
      onStatus: (queue) => updateQueue({ id: queue.id, input: { active: !queue.active } }),
      onCopy: undefined,
    },
  };

  // Selection Lists

  const { data: selectionLists, isLoading: isLoadingSelectionLists } = useSelectionLists();
  const { mutate: deleteSelectionList } = useDeleteSelectionList();
  const selectionListsConfig = {
    ...SELECTION_LISTS,
    Drawer: SelectionListDrawer,
    /** @type {Local.SetupCardsProps['config']} */
    cardConfig: {
      ...CARD_CONFIG(SELECTION_LISTS.id),
      childSetups: 'values',
      /**
       * Handler to delete a selection list
       * @param {string} id
       */
      onDelete: (id) => deleteSelectionList(id),
      onStatus: undefined,
      onCopy: undefined,
      manageChildren: true,
    },
    // override the default search filter
    searchFilterFn: selectionListFilterFn,
  };

  return (
    <div className={classes.content}>
      <SetupAccordion data={actions} isLoading={isLoadingActions} {...actionsConfig} />
      <SetupAccordion data={locations} isLoading={isLoadingLocations} {...locationsConfig} />
      <SetupAccordion data={resources} isLoading={isLoadingResources} {...resourcesConfig} />
      <SetupAccordion data={queues} isLoading={isLoadingQueues} {...queuesConfig} />
      <SetupAccordion data={selectionLists} isLoading={isLoadingSelectionLists} {...selectionListsConfig} />
    </div>
  );
};
